import { logAmplitudeEvent } from 'amplitude/amplitude'
import { DataProviderProxy } from 'ra-core'
import { Dispatch } from 'redux'
import { isActionWhitelisted } from '../actionPermissions'
import { AdaAction } from '../actions'
import { BatteryDesignAssistantArgs, runBatteryDesignAssistantCore } from '../utils/runBatteryDesignAssistantCore'
import { createToolExecutionStateHelpers } from './hooks/useToolExecutionState'

export async function handleRunBatteryDesignAssistant(
  action: AdaAction,
  orgId: number | undefined,
  projectId: number | undefined,
  dataProvider: DataProviderProxy,
  dispatch: Dispatch
) {
  // Check if this action was auto-accepted via whitelist
  const isAutoAccepted = isActionWhitelisted('runBatteryDesignAssistant')

  // Create tool execution state helpers (created outside try so it's available in catch)
  const toolState = createToolExecutionStateHelpers(dispatch, {
    toolType: 'batteryDesignAssistant',
    projectId,
    isAutoAccepted,
    initialContent: 'Getting battery recommendations...',
  })

  try {
    // Get the selected system
    const system = window.editor?.selectedSystem
    if (!system) {
      throw new Error('No system selected. Please select a system in the design.')
    }

    const args = action.args as BatteryDesignAssistantArgs

    console.log('Starting runBatteryDesignAssistant for system:', system.uuid, args)

    logAmplitudeEvent('generic_button_clicked', {
      source: 'Battery Design Assistant Clicked',
      context: 'ada_chat_action',
      system_uuid: system.uuid,
      customer_goal: args.customer_goal,
      orgId: orgId,
    })

    // Set running state
    toolState.setRunning()

    // Use shared core logic
    const result = await runBatteryDesignAssistantCore(args, system.uuid, projectId!, async (postData) => {
      const response = await dataProvider.CUSTOM_POST('recommend_battery_sets', {
        url: `projects/${projectId}/systems/${system.uuid}/recommend_battery_sets/`,
        data: postData,
      })

      if (!response || !response.data || !Array.isArray(response.data)) {
        throw new Error('No battery recommendations returned from the server.')
      }

      return response.data
    })

    // Update the message to show ready state with recommendations
    // Store customerPriority and essentialBackupMetrics for use when loading results
    if (window.debugAdaActions) {
      console.log('Setting ready state with batterySets:', {
        count: result.batterySets.length,
        batterySets: result.batterySets,
        description: result.description,
      })
    }

    toolState.setReady(result.description, {
      batterySets: result.batterySets,
      customerPriority: result.customerPriority,
      essentialBackupMetrics: result.essentialBackupMetrics,
    })

    console.log('Battery design assistant completed, recommendations:', result.batterySets.length)
  } catch (error: any) {
    console.error('Error running battery design assistant:', error)

    // Update message to show error state
    toolState.setError(error?.message || 'Battery design assistant failed', {
      message: error?.message || 'Battery design assistant failed',
    })
  }
}
